import { useState } from 'react'
import {
  Check,
  Zap,
  DollarSign,
  Target,
  Shield,
  Globe,
  BarChart3,
  MessageSquare,
  Brain,
  LayoutGrid,
  Send,
  Search,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Investor Deck v3 (/deck3)
   Refined version: deck content + deck2 layout/imagery. Text left, imagery right.
   Problem & Solution slides: scattered real logo assets loaded from /public/logos/deck.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function Deck3Page() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed top-6 right-8 z-50">
        <span className="text-[10px] font-medium text-zinc-800 uppercase tracking-widest">
          Confidential
        </span>
      </div>
      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : 'pb-32 sm:pb-40'} ${i === SLIDES.length - 1 ? 'mb-8' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT & SHARED
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide { title: string; component: React.FC }

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

function TwoCol({
  tag,
  headline,
  highlight,
  subline,
  children,
  right,
}: {
  tag?: string
  headline: string
  highlight?: string
  subline?: string
  children?: React.ReactNode
  right: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        {tag && <SlideTag>{tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
          {headline}
          {highlight && <span className="text-[#FF2300]">{highlight}</span>}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400">{subline}</p>}
        {children}
      </div>
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[340px]">
        {right}
      </div>
    </div>
  )
}

function GrayBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-zinc-800/40 p-5">
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[14px] text-zinc-400 leading-relaxed">{children}</p>
    </div>
  )
}

/* ── Mocks for imagery ───────────────────────────────────────────────── */
function MockCard({ className = '', glow, children }: { className?: string; glow?: boolean; children: React.ReactNode }) {
  return (
    <div className={`rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-xl ${glow ? 'shadow-[0_0_60px_-12px_rgba(255,35,0,0.2)]' : ''} ${className}`}>
      {children}
    </div>
  )
}

function MockHeroOverlap() {
  return (
    <div className="relative w-full max-w-xl mx-auto flex items-center justify-center">
      <MockCard className="absolute right-0 top-0 w-[72%] max-w-md p-5 opacity-90" glow>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-white">Team Management</span>
          <LayoutGrid className="w-4 h-4 text-zinc-500" />
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-lg bg-white/5 p-2"><span className="text-lg font-semibold text-white">42</span><span className="text-[11px] text-zinc-500 block">Shows</span></div>
          <div className="rounded-lg bg-white/5 p-2"><span className="text-lg font-semibold text-white">53</span><span className="text-[11px] text-zinc-500 block">Tasks</span></div>
        </div>
        <div className="space-y-1">
          {['Charly Manager', 'Promoter'].map((n, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5">
              <div className="w-7 h-7 rounded-full bg-white/10" />
              <span className="text-[12px] text-zinc-300">{n}</span>
            </div>
          ))}
        </div>
      </MockCard>
      <MockCard className="relative left-0 z-10 w-[65%] max-w-sm p-4" glow>
        <div className="flex gap-3 mb-2 border-b border-white/10 pb-2">
          <span className="text-[13px] font-medium text-[#FF2300] border-b-2 border-[#FF2300]">Shows</span>
          <span className="text-[13px] text-zinc-500">Schedule</span>
        </div>
        <div className="text-[11px] text-zinc-500 space-y-0.5">UPCOMING · 31 · SHOWS · 19</div>
        <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-1.5 mt-2 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-[12px] text-zinc-500">Ask anything...</span>
          <Send className="w-3.5 h-3.5 text-[#FF2300] ml-auto" />
        </div>
      </MockCard>
    </div>
  )
}

const FRAGMENTED_LOGOS: { label: string; src: string; imgClass: string; logoColor?: string }[] = [
  { label: 'Email', src: '/logos/deck/gmail.svg', imgClass: 'h-5', logoColor: '#EA4335' },
  { label: 'iMessage', src: '/logos/deck/apple.svg', imgClass: 'h-5' },
  { label: 'Spreadsheets', src: '/logos/deck/googlesheets.svg', imgClass: 'h-5', logoColor: '#0F9D58' },
  { label: 'Slack', src: '/logos/deck/slack.svg', imgClass: 'h-5' },
  { label: 'Asana', src: '/logos/deck/asana-wordmark.svg', imgClass: 'h-4' },
  { label: 'MasterTour', src: '/logos/deck/mastertour.svg', imgClass: 'h-4' },
  { label: 'Spotify for Artists', src: '/logos/deck/spotify-wordmark.svg', imgClass: 'h-4' },
  { label: 'ASCAP', src: '/logos/deck/ascap.svg', imgClass: 'h-5' },
]

function MockFragmentedLogos() {
  const positions = [
    { left: '0%', top: '5%' }, { left: '50%', top: '0%' }, { left: '85%', top: '15%' },
    { left: '10%', top: '42%' }, { left: '78%', top: '38%' },
    { left: '5%', top: '78%' }, { left: '55%', top: '82%' }, { left: '88%', top: '75%' },
  ]
  return (
    <div className="relative w-full max-w-lg h-[320px]">
      {FRAGMENTED_LOGOS.map((logo, i) => (
        <div
          key={i}
          className="absolute rounded-lg border border-white/15 bg-white/90 px-3 py-2 text-[11px] text-zinc-700 whitespace-nowrap shadow-sm"
          style={{ left: positions[i].left, top: positions[i].top }}
        >
          <div className="flex items-center gap-2" style={logo.logoColor ? { color: logo.logoColor } : undefined}>
            <img src={logo.src} alt={logo.label} className={`${logo.imgClass} w-auto object-contain`} />
            <span>{logo.label}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

function MockCentralDizzy() {
  const positions = [
    { left: '8%', top: '8%' }, { left: '75%', top: '5%' }, { left: '5%', top: '42%' }, { left: '82%', top: '40%' },
    { left: '42%', top: '2%' }, { left: '2%', top: '78%' }, { left: '80%', top: '82%' },
  ]
  return (
    <div className="relative w-full max-w-lg h-[300px]">
      {FRAGMENTED_LOGOS.slice(0, 7).map((logo, i) => (
        <div
          key={i}
          className="absolute rounded border border-white/10 bg-white/90 px-2 py-1.5 text-[10px] text-zinc-700 whitespace-nowrap shadow-sm"
          style={{ left: positions[i].left, top: positions[i].top }}
        >
          <div className="flex items-center gap-1.5" style={logo.logoColor ? { color: logo.logoColor } : undefined}>
            <img src={logo.src} alt={logo.label} className={`${logo.imgClass} w-auto object-contain`} />
            <span>{logo.label}</span>
          </div>
        </div>
      ))}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
        {positions.slice(0, 7).map((pos, i) => {
          const cx = parseFloat(pos.left) + 6
          const cy = parseFloat(pos.top) + 4
          return (
            <line key={i} x1={cx} y1={cy} x2={50} y2={50} stroke="rgba(255,255,255,0.25)" strokeWidth="0.3" strokeDasharray="1 1" />
          )
        })}
      </svg>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-2 z-10">
        <div className="w-8 h-8 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <span className="text-base font-semibold text-white">DIZEE</span>
      </div>
    </div>
  )
}

function ProductBoxWithImage({ title, description, mock }: { title: string; description: string; mock: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/20 bg-white/[0.06] backdrop-blur-xl overflow-hidden shadow-lg shadow-black/10">
      <div className="p-4 border-b border-white/[0.08]">
        <h3 className="text-[15px] font-semibold text-white">{title}</h3>
        <p className="text-[12px] text-zinc-400 mt-1">{description}</p>
      </div>
      <div className="p-2 min-h-[80px] flex items-center justify-center bg-white/[0.03] backdrop-blur-sm border-t border-white/[0.06]">
        {mock}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTitle() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-4 w-auto text-white" />
        </div>
        <div className="flex items-center gap-3 mt-6">
          <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-1.5 text-[12px] font-medium text-[#FF2300]">Pre-Seed</span>
          <span className="text-[13px] text-zinc-500">February 2026</span>
        </div>
        <p className="text-sm text-zinc-500 mt-8">www.dizee.com</p>
      </div>
      <div className="relative flex items-center justify-center min-h-[320px]">
        <MockHeroOverlap />
      </div>
    </div>
  )
}

function SlideMission() {
  return (
    <div className="flex flex-col justify-center min-h-[50vh]">
      <SlideTag>Mission</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[56px] font-semibold tracking-tighter text-white leading-[1.08] max-w-4xl">
        The new operating system and{' '}
        <span className="text-[#FF2300]">source of truth</span> for modern music careers.
      </h2>
      <div className="mt-8 max-w-2xl">
        <p className="text-base sm:text-lg text-zinc-400 leading-relaxed">
          So artists, managers, and their teams stay aligned around a single source of truth.
        </p>
      </div>
    </div>
  )
}

function SlideWhatsHappening() {
  return (
    <div>
      <SlideTag>What&apos;s Happening Now</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        The industry has shifted.{' '}
        <span className="text-[#FF2300]">Fundamentally.</span>
      </h2>
      <div className="mt-12 grid sm:grid-cols-2 gap-5">
        <GrayBox title="A new landscape">The music industry is undergoing a seismic technological and cultural transformation.</GrayBox>
        <GrayBox title="Decentralized Power">Labels and traditional gatekeepers no longer manage most careers.</GrayBox>
        <GrayBox title="Independent Era">Independent artists, managers, and small teams now run their own businesses.</GrayBox>
        <GrayBox title="Infrastructure Gap">Responsibility moved to artists and teams, but the operating infrastructure never followed.</GrayBox>
      </div>
    </div>
  )
}

function SlideIndustryTrends() {
  return (
    <TwoCol
      tag="Industry Trends"
      headline="What's coming next."
      subline="The industry has changed. The tools haven't."
      right={
        <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
          <div className="rounded-xl border border-[#FF2300]/25 bg-[#FF2300]/[0.08] p-6">
            <p className="text-4xl font-bold text-[#FF2300]">75.9M</p>
            <p className="text-[12px] text-zinc-400 mt-1">Music Creators (2023)</p>
            <a href="https://routenote.com/blog/nearly-200-million-music-creators-predicted-by-2030/" target="_blank" rel="noopener noreferrer" className="block text-[10px] text-zinc-600 mt-2 hover:text-zinc-500">Source</a>
          </div>
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.08] p-6">
            <p className="text-4xl font-bold text-emerald-400">198.2M</p>
            <p className="text-[12px] text-zinc-400 mt-1">By 2030</p>
            <a href="https://routenote.com/blog/nearly-200-million-music-creators-predicted-by-2030/" target="_blank" rel="noopener noreferrer" className="block text-[10px] text-zinc-600 mt-2 hover:text-zinc-500">Source</a>
          </div>
        </div>
      }
    >
      <div className="mt-8 space-y-4">
        <GrayBox title="New AI Tools">Gen AI music & audiovisual market could rise from €3B to €64B by 2028.</GrayBox>
        <GrayBox title="Barrier to Entry">120,000 new tracks hit streaming services each day (Q1 2023).</GrayBox>
        <GrayBox title="Rise of the Creators">Creator Economy: $252.33B (2025).</GrayBox>
        <GrayBox title="Fragmented Ecosystem">Performance rights $2.9B (2024) — another revenue stream to manage.</GrayBox>
      </div>
    </TwoCol>
  )
}

function SlideProblemCurrentTools() {
  return (
    <TwoCol
      tag="The Problem — Current State"
      headline="Music business is still "
      highlight="outdated."
      subline="Manual and disconnected. No unified system to manage the modern music business."
      right={<MockFragmentedLogos />}
    >
      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="flex items-center gap-2 mb-2"><Zap className="w-4 h-4 text-zinc-500" /><span className="text-sm font-semibold text-white">Manual Processes</span></div>
          <p className="text-[13px] text-zinc-400">Nothing automated or connected.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-zinc-500" /><span className="text-sm font-semibold text-white">Fragmented Communication</span></div>
          <p className="text-[13px] text-zinc-400">Spreadsheets, texts, email — critical decisions in iMessage nobody can search.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-zinc-500" /><span className="text-sm font-semibold text-white">Legacy Tools</span></div>
          <p className="text-[13px] text-zinc-400">Built for a different era. Can&apos;t keep up.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-zinc-500" /><span className="text-sm font-semibold text-white">Disconnected Systems</span></div>
          <p className="text-[13px] text-zinc-400">Data in a dozen platforms. No way to connect the dots.</p>
        </div>
      </div>
    </TwoCol>
  )
}

function SlideProblemHighLevel() {
  return (
    <div>
      <SlideTag>The Problem</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Artists lack real-time visibility{' '}
        <span className="text-[#FF2300]">into what&apos;s happening in their career.</span>
      </h2>
      <div className="mt-12 max-w-3xl space-y-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-red-400/60 shrink-0 mt-0.5" />
          <p className="text-base text-zinc-300">Managers don&apos;t have a centralized system, the time, or reliable access to data needed to make informed decisions.</p>
        </div>
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-red-400/60 shrink-0 mt-0.5" />
          <p className="text-base text-zinc-300">Today&apos;s tools are slow, fragmented, and static — easy to miss moments that matter. By the time data is gathered, the opportunity has passed.</p>
        </div>
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-red-400/60 shrink-0 mt-0.5" />
          <p className="text-base text-zinc-300">Communication is chaotic — iMessage, email, text threads, DMs. Nothing is tracked, searchable, or connected.</p>
        </div>
      </div>
    </div>
  )
}

const REAL_CONVOS_BEFORE_AFTER = [
  {
    question: 'Do we have an updated ticket count for the NY show?',
    before: [
      { from: 'Mngr', msg: "I don't see it in the email, let me ask the agents." },
      { from: 'Mngr', msg: 'Ok they responded and hit the promoter to get it.' },
      { from: 'Mngr', msg: "Next day; Ok promoter sent back to them, it's 583 so 70% sold." },
    ],
    after: [
      { from: 'Mngr', msg: 'ya all the counts are in dizee, you can check it anytime and see the latest counts.' },
      { from: 'Artist', msg: "oh ok sick, so easy, now I dont have to ask you everyday haha" },
    ],
  },
  {
    question: 'How are sales looking for the LA show, are we pacing good?',
    before: [],
    after: [
      { from: 'Mngr', msg: "Ya in dizee you can actually see how all the shows are pacing and compare it to the last time you played there." },
      { from: 'Artist', msg: "Oh wow, that's so helpful!" },
    ],
  },
  {
    question: 'Did the Chicago show get confirmed?',
    before: [
      { from: 'Mngr', msg: "I think so but let me ask, the agents never confirmed." },
    ],
    after: [
      { from: 'Mngr', msg: 'I got the notification that Chicago got confirmed, stocked for that one' },
    ],
  },
]
function SlideExampleMessages() {
  return (
    <div>
      <SlideTag>Real Conversations</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tighter text-white">
        Before DIZEE vs after DIZEE.
      </h2>
      <div className="mt-10 grid gap-8 w-full max-w-none">
        {REAL_CONVOS_BEFORE_AFTER.map((conv, idx) => (
          <div key={idx} className="space-y-3 w-full">
            <p className="text-[13px] text-zinc-400 italic">&ldquo;{conv.question}&rdquo;</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
              <div className="space-y-2 min-w-0">
                <p className="text-[11px] font-semibold text-red-400/90 uppercase tracking-wider">Before DIZEE</p>
                {conv.before.length > 0 ? conv.before.map((m, i) => (
                  <div key={i} className="rounded-2xl rounded-bl-sm px-4 py-2.5 w-full min-w-0 bg-red-500/10 border border-red-500/20 backdrop-blur-md">
                    <span className="text-[11px] font-semibold text-red-400/90">{m.from}</span>
                    <p className="text-[13px] text-zinc-300 mt-0.5">&ldquo;{m.msg}&rdquo;</p>
                  </div>
                )) : (
                  <div className="rounded-2xl rounded-bl-sm px-4 py-2.5 w-full min-w-0 bg-red-500/10 border border-red-500/20 backdrop-blur-md">
                    <p className="text-[12px] text-zinc-500 italic">No single place to check — back-and-forth across email and agents.</p>
                  </div>
                )}
              </div>
              <div className="space-y-2 min-w-0">
                <p className="text-[11px] font-semibold text-emerald-400/90 uppercase tracking-wider">After DIZEE</p>
                {conv.after.map((m, i) => (
                  <div key={i} className="rounded-2xl rounded-bl-sm px-4 py-2.5 w-full min-w-0 bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-md">
                    <span className="text-[11px] font-semibold text-emerald-400/90">{m.from}</span>
                    <p className="text-[13px] text-zinc-300 mt-0.5">&ldquo;{m.msg}&rdquo;</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideCentralizes() {
  return (
    <TwoCol
      tag="The Solution"
      headline="A single operating system and source of truth "
      highlight="for modern music careers."
      subline="Real-time clarity. Move faster. Capture revenue as it happens."
      right={<MockCentralDizzy />}
    >
      <div className="mt-8 grid sm:grid-cols-1 gap-4">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <BarChart3 className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">Real-Time Clarity</h3>
          <p className="text-[14px] text-zinc-400">Unified view of releases, shows, finances, team — in one place.</p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <Brain className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">Smarter Decisions</h3>
          <p className="text-[14px] text-zinc-400">AI surfaces opportunities as they happen so you can act, not react.</p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <Target className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">End-to-End Management</h3>
          <p className="text-[14px] text-zinc-400">Catalog, touring, team, projects — all connected.</p>
        </div>
      </div>
    </TwoCol>
  )
}

const PRODUCT_ITEMS: { title: string; desc: string; mock: React.ReactNode }[] = [
  {
    title: 'Dashboard',
    desc: 'Real-time view of your entire music career in one place',
    mock: (
      <div className="w-full text-left p-2 space-y-1.5">
        <div className="flex gap-2 text-[10px] text-zinc-500 border-b border-white/10 pb-1"><span className="text-[#FF2300] font-medium">Shows</span><span>Schedule</span><span>Tasks</span></div>
        <div className="grid grid-cols-2 gap-1"><div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-sm font-semibold text-white">42</span><span className="text-[10px] block text-zinc-500">Shows</span></div><div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-sm font-semibold text-white">53</span><span className="text-[10px] block text-zinc-500">Tasks</span></div></div>
      </div>
    ),
  },
  {
    title: 'Teams',
    desc: 'Connect team members and streamline communication',
    mock: (
      <div className="w-full text-left p-2 space-y-1">
        {['Charly Manager', 'Promoter'].map((n, i) => (
          <div key={i} className="flex items-center gap-2 py-1 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 px-2"><div className="w-6 h-6 rounded-full bg-white/10" /><span className="text-[11px] text-zinc-400">{n}</span></div>
        ))}
      </div>
    ),
  },
  {
    title: 'Bookings',
    desc: 'Ticket counts, guarantee tracking, promoter CRM',
    mock: (
      <div className="w-full text-left p-2">
        <div className="text-[11px] text-zinc-500 mb-1">Tempo · Cap 400 · Sold 328</div>
        <div className="h-1.5 rounded-full bg-white/10 backdrop-blur-sm"><div className="h-full rounded-full bg-[#FF2300] w-[82%]" /></div>
        <div className="text-[10px] text-[#FF2300] mt-0.5">82%</div>
      </div>
    ),
  },
  {
    title: 'Catalog & Releases',
    desc: 'Discography, metadata, splits, credits',
    mock: (
      <div className="w-full text-left p-2 space-y-1">
        <div className="text-[10px] text-zinc-500 border-b border-white/10 pb-1">Releases · Splits</div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-[11px] text-zinc-400">Single v2 · Splits 50/50</span></div>
      </div>
    ),
  },
  {
    title: 'Tour Manager',
    desc: 'Touring operations, travel, communication',
    mock: (
      <div className="w-full text-left p-2 space-y-1">
        <div className="flex gap-2 text-[10px] text-zinc-500 border-b border-white/10 pb-1"><span>Routing</span><span>Travel</span><span>Crew</span></div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-[11px] text-zinc-400">Denver → LA → Chicago</span></div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-[11px] text-zinc-400">Crew ETA 4:30 PM</span></div>
      </div>
    ),
  },
  {
    title: 'Tasks & Projects',
    desc: 'Tasks, notes, files, collaboration',
    mock: (
      <div className="w-full text-left p-2 space-y-1">
        <div className="flex gap-2 text-[10px] text-zinc-500"><span className="text-[#FF2300] font-medium">3</span> due today</div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5 flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#FF2300]" /><span className="text-[11px] text-zinc-400">Finalize splits</span></div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5 flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-zinc-500" /><span className="text-[11px] text-zinc-400">Send artwork</span></div>
      </div>
    ),
  },
  {
    title: 'AI Intelligence',
    desc: 'At-risk shows, pacing vs last time, bonuses. Factual insights in context.',
    mock: (
      <div className="w-full text-left p-3 space-y-2">
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium border-b border-white/10 pb-1.5">Insights</div>
        <div className="rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 px-2.5 py-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            <span className="text-[11px] text-zinc-300">LA pacing 20% slower than last LA</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-zinc-500">990 sold</span>
            <span className="text-[#FF2300] font-medium">$2K bonus at 1,000</span>
          </div>
          <div className="h-1 rounded-full bg-white/10"><div className="h-full rounded-full bg-[#FF2300]/80 w-[99%]" /></div>
        </div>
      </div>
    ),
  },
  {
    title: '24/7 Sync',
    desc: 'Google Sheets, email, platform integrations',
    mock: (
      <div className="w-full text-left p-3 space-y-2">
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium border-b border-white/10 pb-1.5">Connected</div>
        <div className="space-y-1.5">
          {[
            { label: 'Google Sheets', status: 'Live', color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' },
            { label: 'Email', status: 'Synced', color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' },
            { label: 'Last sync', status: '2m ago', color: 'bg-white/5 border-white/10 text-zinc-500' },
          ].map((row, i) => (
            <div key={i} className={`rounded-lg border px-2 py-1.5 flex items-center justify-between text-[11px] backdrop-blur-sm ${row.color}`}>
              <span className="text-zinc-300">{row.label}</span>
              <span className="font-medium">{row.status}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
]
function SlideProduct() {
  return (
    <div>
      <SlideTag>Product</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white">
        A unified platform that centralizes your career.
      </h2>
      <p className="mt-2 text-zinc-400">DIZEE-style modules: Dashboard, Teams, Bookings, Catalog, Tour Manager, Tasks, AI Intelligence, 24/7 Sync.</p>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PRODUCT_ITEMS.map((item, i) => (
          <ProductBoxWithImage key={i} title={item.title} description={item.desc} mock={item.mock} />
        ))}
      </div>
    </div>
  )
}

function SlideMarketSize() {
  return (
    <div>
      <SlideTag>Market Size</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        A massive, underserved market{' '}
        <span className="text-[#FF2300]">growing every year.</span>
      </h2>
      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        {[
          { label: 'TAM — Creator Infrastructure Software', stat: '$7.5B', sub: '3% of $252B Global Creator Economy (2025)', color: 'border-[#FF2300]/20 bg-[#FF2300]/[0.04]' },
          { label: 'SAM — Music (Initial Wedge)', stat: '$750M', sub: '~13M artists tracked globally (2025)', color: 'border-violet-500/20 bg-violet-500/[0.04]' },
          { label: 'SOM — Professional Artist Teams', stat: '$15–20M', sub: 'ARR · Managed / team-operated artists', color: 'border-emerald-500/20 bg-emerald-500/[0.04]' },
        ].map((s, i) => (
          <div key={i} className={`rounded-xl border ${s.color} p-6`}>
            <h3 className="text-[15px] font-semibold text-white mb-1">{s.label}</h3>
            <p className="text-3xl font-bold text-white">{s.stat}</p>
            <p className="text-[12px] text-zinc-500 mt-1">{s.sub}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideBusinessModel() {
  return (
    <div>
      <SlideTag>Business Model</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Simple subscription.{' '}
        <span className="text-[#FF2300]">Predictable revenue.</span>
      </h2>
      <div className="mt-10 grid lg:grid-cols-3 gap-4">
        {[
          { tier: 'Artist', price: '$45', note: '/mo', audience: 'Single user' },
          { tier: 'Artist Pro', price: '$75', note: '/mo', audience: 'Single user', popular: true },
          { tier: 'Enterprise', price: '$125', note: '/mo', audience: '2+ users ($15 per additional seat)' },
        ].map((p, i) => (
          <div key={i} className={`rounded-xl border p-5 ${p.popular ? 'border-[#FF2300]/30 bg-[#FF2300]/[0.06]' : 'border-white/[0.06] bg-white/[0.02]'}`}>
            <h3 className="text-sm font-semibold text-white">{p.tier}</h3>
            <p className="text-2xl font-bold text-white mt-1">{p.price}<span className="text-[12px] font-normal text-zinc-500 ml-1">{p.note}</span></p>
            <p className="text-[11px] text-[#FF2300] font-medium mt-1">{p.audience}</p>
          </div>
        ))}
      </div>
      <div className="mt-8">
        <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Additional revenue streams</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-emerald-400/80 shrink-0" />
            <span className="text-[13px] text-zinc-400">AI usage credits</span>
          </div>
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-emerald-400/80 shrink-0" />
            <span className="text-[13px] text-zinc-400">Advanced ingestion add-ons</span>
          </div>
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-emerald-400/80 shrink-0" />
            <span className="text-[13px] text-zinc-400">Integrations marketplace</span>
          </div>
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-emerald-400/80 shrink-0" />
            <span className="text-[13px] text-zinc-400">Financial tools (future)</span>
          </div>
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-emerald-400/80 shrink-0" />
            <span className="text-[13px] text-zinc-400">Sponsorship intelligence (future)</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function SlideGTM() {
  const phases = [
    { phase: 'Phase 1', title: 'Founder-Led Wedge', timeline: 'Months 0–6', target: 'First 200 accounts', items: ['Direct outreach to independent managers', '1:1 onboarding', 'Early product feedback'] },
    { phase: 'Phase 2', title: 'Signal Amplification', timeline: 'Months 6–12', target: '500–700 accounts', items: ['Case studies', 'Referral incentives', 'Partnerships', 'Testimonials'] },
    { phase: 'Phase 3', title: 'Scalable Acquisition', timeline: 'Months 12–24', target: '1,500+ accounts', items: ['Content + education', 'Workflow partnerships', 'Referral engine', 'API integrations'] },
  ]
  return (
    <div>
      <SlideTag>Go-To-Market</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Three phases.{' '}
        <span className="text-[#FF2300]">Founder-led to scalable.</span>
      </h2>
      <div className="mt-12 grid lg:grid-cols-3 gap-6">
        {phases.map((p, i) => (
          <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
            <span className="text-[11px] font-mono text-[#FF2300]/50">{p.phase}</span>
            <h3 className="text-[16px] font-semibold text-white mt-1">{p.title}</h3>
            <p className="text-[11px] text-zinc-600">{p.timeline} · {p.target}</p>
            <ul className="mt-4 space-y-2">
              {p.items.map((item, j) => (
                <li key={j} className="flex items-start gap-2">
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />
                  <span className="text-[13px] text-zinc-400">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideCompetition() {
  /* Same chart as Deck (DeckPage). Artist Growth moved toward Notion (more intelligent, more 360). */
  const competitors: { name: string; x: number; y: number; highlight?: boolean }[] = [
    { name: 'Venice Music', x: 18, y: 22 },
    { name: 'Artist Growth', x: 50, y: 62 },
    { name: 'IndieFlow', x: 38, y: 20 },
    { name: 'Monday', x: 28, y: 72 },
    { name: 'Asana', x: 38, y: 68 },
    { name: 'Slack', x: 48, y: 74 },
    { name: 'Notion', x: 58, y: 70 },
    { name: 'DIZEE', x: 85, y: 88, highlight: true },
  ]
  return (
    <div>
      <SlideTag>Competition</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        From static software{' '}
        <span className="text-[#FF2300]">to an intelligent operating system.</span>
      </h2>
      <div className="mt-10 relative w-full max-w-[700px] mx-auto">
        <svg viewBox="0 0 600 520" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
          <line x1="300" y1="40" x2="300" y2="500" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
          <line x1="20" y1="270" x2="580" y2="270" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
          <text x="20" y="265" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600"><tspan fill="#FF2300">X: </tspan><tspan fill="white">Static Software</tspan></text>
          <text x="580" y="265" textAnchor="end" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">360° Artist Career</text>
          <text x="300" y="28" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600"><tspan fill="#FF2300">Y: </tspan><tspan fill="white">Intelligent Operating System</tspan></text>
          <text x="300" y="512" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">Disconnected Workflows</text>
          {competitors.map((c, i) => {
            const cx = 20 + (c.x / 100) * 560
            const cy = 500 - (c.y / 100) * 460
            return (
              <g key={i}>
                <circle cx={cx} cy={cy} r={c.highlight ? 14 : 8} fill={c.highlight ? '#FF2300' : 'rgba(255,255,255,0.8)'} />
                <text x={cx} y={cy + (c.highlight ? 28 : 22)} textAnchor="middle" fill={c.highlight ? '#FF2300' : 'rgba(255,255,255,0.6)'} fontSize={c.highlight ? '14' : '12'} fontWeight={c.highlight ? '700' : '600'}>{c.name}</text>
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  )
}

function SlideAdvantages() {
  const bullets = [
    'Built by a 2x Grammy-nominated manager — 2B+ streams. Deep industry understanding.',
    'CEO with proven credibility and active Billboard-charting label.',
    'Rare hybrid team: music insiders + modern product builders.',
    'DIZEE AI informed by 15+ years lived experience — music-native intelligence.',
    'First-mover in a new category. Defining the space.',
  ]
  return (
    <div>
      <SlideTag>Why DIZEE Wins</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Why DIZEE wins.
      </h2>
      <ul className="mt-10 space-y-4 max-w-3xl">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-[15px] text-zinc-300">{b}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const TEAM_MEMBERS = [
  { name: 'Ryan Thomas', role: 'CEO', points: ['15+ years dance music', 'Grammy-nominated, platinum artists', 'Global relationships'] },
  { name: 'Edward Contreras', role: 'CTO', points: ['Technologist, product builder', 'AI, music tech, integrations', "Architect of DIZEE's platform"] },
  { name: 'Aaron P. Wong', role: 'COO', points: ['Founder Super73', 'Scaling hardware + social', 'Venture funding experience'] },
  { name: 'Owais Ghaus', role: 'CMO', points: ['Artist, marketer, technologist', 'Industry connections', 'Content + creator marketing'] },
  { name: 'Christopher Alba', role: 'Creative direction', points: ['Brand strategy and visual identity', 'Music & entertainment partnerships'] },
]
function SlideTeam() {
  return (
    <div>
      <SlideTag>Team</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
        The team building this.
      </h2>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch">
        {TEAM_MEMBERS.map((t, i) => (
          <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 flex flex-col min-h-[200px]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF2300]/30 to-[#FF4500]/20 border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                <span className="text-[13px] font-semibold text-white">{t.name.split(' ').map((n) => n[0]).join('')}</span>
              </div>
              <div className="min-w-0">
                <h3 className="text-[14px] font-semibold text-white">{t.name}</h3>
                <p className="text-[11px] text-[#FF2300] font-medium">{t.role}</p>
              </div>
            </div>
            <ul className="space-y-2 flex-1">
              {t.points.map((point, j) => (
                <li key={j} className="flex items-start gap-2">
                  <div className="mt-1.5 h-1 w-1 rounded-full bg-zinc-600 shrink-0" />
                  <p className="text-[12px] text-zinc-400">{point}</p>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideAsk() {
  const uses = [
    'Product–market fit with real artists and managers',
    'Core platform development',
    'Deep integrations',
    'Marquee artist onboarding',
    'Founding team & execution',
    'Infrastructure & compliance',
  ]
  return (
    <div>
      <SlideTag>The Ask</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Raising <span className="text-[#FF2300]">$2.5M</span> to accelerate.
      </h2>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {uses.map((u, i) => (
          <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
            <p className="text-[14px] text-zinc-300">{u}</p>
          </div>
        ))}
      </div>
      <div className="mt-12 rounded-xl border-2 border-[#FF2300]/30 bg-[#FF2300]/[0.08] p-8 w-full">
        <p className="text-xl sm:text-2xl font-semibold text-white text-center">
          DIZEE is building the modern operating system the music industry has been missing.
        </p>
        <p className="mt-3 text-[14px] text-zinc-400 text-center">
          The team is built. The product is in motion. Now it&apos;s about capturing the market.
        </p>
      </div>
    </div>
  )
}

function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 leading-snug max-w-2xl">
        The new operating system and source of truth for modern music careers.
      </p>
      <div className="mt-12 space-y-2">
        <p className="text-[14px] text-[#FF2300] font-medium">partners@dizee.com</p>
        <p className="text-[13px] text-zinc-500">www.dizee.com</p>
      </div>
    </div>
  )
}

function SlideAppendix() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-[11px] font-semibold text-zinc-700 uppercase tracking-[0.3em]">Appendix</p>
      <div className="mt-4 w-16 h-px bg-zinc-800" />
    </div>
  )
}

function SlideWhyNow() {
  return (
    <div>
      <SlideTag>Why Now</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        A generational shift.{' '}
        <span className="text-[#FF2300]">The timing is now.</span>
      </h2>
      <div className="mt-12 grid sm:grid-cols-2 gap-6">
        <GrayBox title="A generational industry shift">Independent artists are the fastest-growing segment. Non-major labels reached 29.7% market share in 2024.</GrayBox>
        <GrayBox title="The decline of gatekeepers">DIY creators expect label-grade tools without label control. A unified career OS wasn&apos;t possible before.</GrayBox>
        <GrayBox title="Complexity is increasing">More music is released in a single day than in all of 1989. Touring and marketing are more complex than ever.</GrayBox>
        <GrayBox title="Technology readiness has converged">AI, cloud, and APIs have matured. Building a true career operating system is finally possible.</GrayBox>
      </div>
      <p className="mt-10 text-lg font-medium text-white max-w-2xl">The industry is ready for a single, modern career operating system.</p>
    </div>
  )
}

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Mission', component: SlideMission },
  { title: "What's Happening Now", component: SlideWhatsHappening },
  { title: 'Industry Trends', component: SlideIndustryTrends },
  { title: 'Current State', component: SlideProblemCurrentTools },
  { title: 'Problem', component: SlideProblemHighLevel },
  { title: 'Real Conversations', component: SlideExampleMessages },
  { title: 'Solution', component: SlideCentralizes },
  { title: 'Product', component: SlideProduct },
  { title: 'Market Size', component: SlideMarketSize },
  { title: 'Business Model', component: SlideBusinessModel },
  { title: 'Go-To-Market', component: SlideGTM },
  { title: 'Competition', component: SlideCompetition },
  { title: 'Why DIZEE Wins', component: SlideAdvantages },
  { title: 'Team', component: SlideTeam },
  { title: 'The Ask', component: SlideAsk },
  { title: 'Closing', component: SlideClosing },
  { title: 'Appendix', component: SlideAppendix },
  { title: 'Why Now', component: SlideWhyNow },
]
